import type { GameSource } from "../game/controller/source/GameSource.js";
import { z } from "zod";
import type { LobbySummary } from "../lobby/LobbySummary.js";
import type { Bot } from "../game/Bot.js";
import type { GameAnalysis } from "../analysis/GameAnalysis.js";
import type { Engine } from "../../royalur/engine/Engine.js";
import type { Optional } from "../../util/Optional.js";
import type { Game } from "../../royalur/Game.js";
import type { GameMode } from "../game/GameMode.js";
import type { GameSettings } from "../../royalur/model/GameSettings.js";
import type { BotType } from "../game/BotType.js";
import type { Ruleset } from "../game/Ruleset.js";
import type { APIEarnedAchievement } from "./api_schema.js";


export const CreateGameResponse = z.object({
    lobby_id: z.string(),
});
export type CreateGameResponse = z.infer<typeof CreateGameResponse>;


export const AvailableBot = z.object({
    type: z.string(),
    settings: z.string(),
});
export type AvailableBot = z.infer<typeof AvailableBot>;


export const WaitingPlayerStateResponse = z.object({
    mode: z.string(),
    settings: z.string(),
    waiting_count: z.number(),
});
export type WaitingPlayerStateResponse = z.infer<typeof WaitingPlayerStateResponse>;


export const ListLiveLobbiesResponse = z.object({
    available_bots: z.array(AvailableBot),
    waiting_players: z.array(WaitingPlayerStateResponse),
    started_lobbies_is_exhaustive: z.boolean(),
    started_lobbies: z.array(z.record(z.string(), z.any())),
});
export type ListLiveLobbiesResponse = z.infer<typeof ListLiveLobbiesResponse>;


export const RecalculateUserStatsResponse = z.object({
    user_public_id: z.string(),
});
export type RecalculateUserStatsResponse = z.infer<typeof RecalculateUserStatsResponse>;


export const ClearGameRatingResponse = z.object({
    changed: z.boolean(),
    rating_cleared: z.boolean(),
    added_rating_back_to_loser: z.boolean(),
});
export type ClearGameRatingResponse = z.infer<typeof ClearGameRatingResponse>;


export const CheckCanCreateResponse = z.object({
    can_create: z.boolean(),
});
export type CheckCanCreateResponse = z.infer<typeof CheckCanCreateResponse>;


export interface WaitingPlayerState {
    mode: GameMode;
    settings: Ruleset;
    waitingCount: number;
}


export interface LobbyManagerState {
    availableBots: Bot[];
    waitingPlayers: WaitingPlayerState[];
    startedLobbies: LobbySummary[];
    startedLobbiesIsExhaustive: boolean;
}


export abstract class GameAPI {

    abstract createGame(
        gameMode: GameMode,
        settings: GameSettings,
        botType: BotType | null
    ): Promise<CreateGameResponse>;

    abstract uploadGame(source: GameSource): Promise<APIEarnedAchievement[]>;

    abstract analyseGame(rules: Engine, gameID: string): Promise<Optional<GameAnalysis>>;

    abstract getPandaGame(): Promise<Game>;

    abstract getLobbyManagerState(): Promise<LobbyManagerState>;

    abstract getLobbyWebSocketURL(lobbyID: string): string;

    abstract getMatchmakingWebSocketURL(): string;

    abstract recalculateUserStats(userPublicID: string): Promise<void>;

    abstract clearGameRating(gameID: string, clearRating: boolean, addRatingBackToLoser: boolean): Promise<ClearGameRatingResponse>;

    abstract checkCanCreateLobby(
        gameMode: GameMode,
        settings: GameSettings,
        botType: BotType | null
    ): Promise<CheckCanCreateResponse>;
}
